// src/components/IdoneitaDitte.js
// ═══════════════════════════════════════════════════════════════════════════
// DITTE ESTERNE IN REGOLA? (ottobre 2026, W413 — piano Gold)
// ═══════════════════════════════════════════════════════════════════════════
// useIdoneitaDitte(): per chi può consultare i Documenti dei subappaltatori, con il piano
// Gold, la mappa «id ditta → semaforo e documenti scaduti/mancanti» (utils/documentiSubappaltatori.js,
// idoneitaDitte). Senza piano o senza permesso restituisce null: nessun avviso, nessun errore.
// BadgeIdoneita: il pallino accanto al nome della ditta, con il dettaglio al passaggio.
// ═══════════════════════════════════════════════════════════════════════════
import React, { useEffect, useMemo, useState } from "react";
import { collection, query, where, onSnapshot, doc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useCanSee } from "../hooks/useCanSee";
import { usePiano } from "../utils/usePiano";
import { statoModulo } from "../utils/pianiNexiora";
import { idoneitaDitte, testoProblemiDitta } from "../utils/documentiSubappaltatori";

export function useIdoneitaDitte(orgId) {
  const { isOrgAdmin, isPlatformOwner } = useAuth();
  const canSee = useCanSee();
  const { stato } = usePiano(orgId);
  const verifica = !!(isOrgAdmin || isPlatformOwner || canSee("documenti-subappaltatori-verifica"));
  const consulta = verifica || canSee("documenti-subappaltatori");
  const attivo = !!orgId && consulta && statoModulo("subappaltatori", stato) === "full";
  const [ditte, setDitte] = useState(null);
  const [documenti, setDocumenti] = useState(null);
  const [impostazioni, setImpostazioni] = useState({});
  useEffect(() => {
    if (!attivo) { setDitte(null); setDocumenti(null); return undefined; }
    const u = [
      onSnapshot(query(collection(db, "subappaltatori"), where("organizationId", "==", orgId)), s => setDitte(s.docs.map(d => ({ id: d.id, ...d.data() }))), () => setDitte(null)),
      // chi non verifica legge solo i documenti non sanitari: la query deve dirlo (regole)
      onSnapshot(query(collection(db, "documentiSubappaltatori"), where("organizationId", "==", orgId), ...(verifica ? [] : [where("sanitario", "==", false)])),
        s => setDocumenti(s.docs.map(d => ({ id: d.id, ...d.data() }))), () => setDocumenti(null)),
      onSnapshot(doc(db, "impostazioniDocumentiSubappaltatori", orgId), s => setImpostazioni(s.exists() ? s.data() : {}), () => setImpostazioni({})),
    ];
    return () => u.forEach(f => f());
  }, [attivo, orgId, verifica]);
  return useMemo(() => (ditte && documenti ? idoneitaDitte({ ditte, documenti, impostazioniDoc: impostazioni, soloNonSanitari: !verifica }) : null), [ditte, documenti, impostazioni, verifica]);
}

/** Pallino accanto alla ditta: rosso con i documenti che non vanno, giallo se in scadenza. */
export function BadgeIdoneita({ info, compatto = false }) {
  if (!info || info.semaforo.colore === "verde") return null;
  const rosso = info.semaforo.colore === "rosso";
  const testo = rosso ? testoProblemiDitta(info.problemi) : info.semaforo.label;
  return (
    <span title={testo} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999, whiteSpace: compatto ? "nowrap" : "normal",
      background: rosso ? "#fee2e2" : "#fef3c7", color: rosso ? "#b91c1c" : "#92400e" }}>
      {rosso ? "🔴" : "🟡"} {compatto ? (rosso ? "Documenti non in regola" : "Documenti in scadenza") : testo}
    </span>
  );
}
